import { useEffect, useRef, useState, Fragment, type ReactNode } from 'react';
import { MessageCircle, X, Send, Loader2, UserRound, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';
import { useGuidance } from '../hooks/useGuidance';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  /** Assistant turns only: show the "contact case manager" button under this message. */
  offerHandoff?: boolean;
}

// Inline **bold** -> <strong>. The bot is told to use light markdown; without
// this the client would see literal asterisks.
function renderInline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

// Minimal markdown renderer for assistant answers: paragraphs, ordered and
// unordered lists, and inline bold. Deliberately tiny (no dependency) — the
// bot only emits these constructs.
function renderAnswer(content: string): ReactNode {
  const lines = content.split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const items = list.items.map((it, i) => <li key={i}>{renderInline(it)}</li>);
    blocks.push(
      list.ordered ? (
        <ol key={blocks.length} className="list-decimal pl-5 space-y-1 my-1">{items}</ol>
      ) : (
        <ul key={blocks.length} className="list-disc pl-5 space-y-1 my-1">{items}</ul>
      ),
    );
    list = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    const ol = line.match(/^\d+[.)]\s+(.*)$/);
    const ul = line.match(/^[-*•]\s+(.*)$/);
    if (ol) {
      if (!list || !list.ordered) { flush(); list = { ordered: true, items: [] }; }
      list.items.push(ol[1]);
    } else if (ul) {
      if (!list || list.ordered) { flush(); list = { ordered: false, items: [] }; }
      list.items.push(ul[1]);
    } else {
      flush();
      if (line) blocks.push(<p key={blocks.length} className="my-1 first:mt-0 last:mb-0">{renderInline(line)}</p>);
    }
  }
  flush();
  return blocks;
}

const GREETING =
  "Hi! I'm the ADI portal assistant. I can answer common questions about your visa and help you find your way around the portal. For anything specific to your situation, I'll point you to your case manager.";

/**
 * Floating "Ask ADI" assistant. Matter-scoped: it sends the active matter's id
 * to the faq-assistant edge function, which grounds answers in the approved FAQ
 * knowledge base for that visa subclass plus a guide to the portal. It does not
 * give personalised migration advice (the function's guardrails enforce this).
 *
 * Mounted in PortalLayout and shown only when there is an active matter and the
 * viewer is in client mode (real client, or staff impersonating a client).
 */
export default function FaqAssistant() {
  const { activeMatter, caseManager } = useMatter();
  const { snapshot, form } = useGuidance();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [handoff, setHandoff] = useState<'idle' | 'sending' | 'sent'>('idle');
  const scrollRef = useRef<HTMLDivElement>(null);

  const cmFirst = caseManager?.given_names?.trim() || 'your case manager';

  // Keep the latest message in view.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, loading]);

  if (!activeMatter) return null;

  async function send() {
    const message = input.trim();
    if (!message || loading) return;
    setError(null);
    setInput('');
    const history = turns.slice(-6);
    const next = [...turns, { role: 'user' as const, content: message }];
    setTurns(next);
    setLoading(true);
    try {
      // Where the client is right now (route, section, active field, errors,
      // outstanding items). PII-safe: labels only, no field values. Lets the
      // assistant answer "what do I put here?" without the client explaining.
      const context = snapshot();
      const { data, error: fnErr } = await supabase.functions.invoke('faq-assistant', {
        body: { matter_id: activeMatter!.id, message, history, context },
      });
      if (fnErr) throw fnErr;
      const res = data as { answer?: string; offer_handoff?: boolean };
      const answer = res?.answer?.trim();
      if (!answer) throw new Error('empty');
      setTurns((t) => [...t, { role: 'assistant', content: answer, offerHandoff: !!res.offer_handoff }]);
    } catch {
      setError("Sorry, I couldn't answer that just now. Please try again, or message your case manager.");
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  // One-click handoff: email the case manager the client's latest question plus
  // recent chat context, reply-to the client. No leaving the portal.
  async function sendToCaseManager() {
    if (handoff !== 'idle') return;
    const lastUser = [...turns].reverse().find((t) => t.role === 'user');
    const question = lastUser?.content?.trim() || 'I would like to speak with my case manager.';
    const context = turns
      .slice(-6)
      .map((t) => `${t.role === 'user' ? 'Me' : 'Assistant'}: ${t.content}`)
      .join('\n');
    setHandoff('sending');
    try {
      const { error: fnErr } = await supabase.functions.invoke('contact-case-manager', {
        body: { matter_id: activeMatter!.id, question, context },
      });
      if (fnErr) throw fnErr;
      setHandoff('sent');
    } catch {
      setHandoff('idle');
      setError("Couldn't send that just now. Please try again in a moment.");
    }
  }

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[#1A8DC7] px-5 py-3 text-white shadow-lg hover:bg-[#157aac] transition-colors"
          aria-label="Open the ADI assistant"
        >
          <MessageCircle size={20} />
          <span className="text-sm font-medium">Ask ADI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-40 flex w-[min(92vw,380px)] flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 max-h-[min(80vh,560px)]">
          {/* Header */}
          <div className="flex items-center justify-between rounded-t-2xl bg-[#0B2341] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle size={18} />
              <span className="text-sm font-semibold">ADI Assistant</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close the ADI assistant"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <div className="rounded-2xl rounded-tl-sm bg-slate-100 px-3 py-2 text-sm text-slate-700">
              {GREETING}
            </div>
            {turns.map((t, i) =>
              t.role === 'user' ? (
                <div
                  key={i}
                  className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-[#1A8DC7] px-3 py-2 text-sm text-white whitespace-pre-wrap"
                >
                  {t.content}
                </div>
              ) : (
                <Fragment key={i}>
                  <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-3 py-2 text-sm text-slate-700 leading-relaxed">
                    {renderAnswer(t.content)}
                  </div>
                  {/* Contextual handoff: only under the latest message where the
                      bot pointed the client to their case manager. */}
                  {t.offerHandoff && i === turns.length - 1 && (
                    handoff === 'sent' ? (
                      <div className="mr-auto flex items-center gap-2 text-xs text-emerald-700 px-1">
                        <Check size={14} className="shrink-0" />
                        <span>Sent to {cmFirst}. They'll be in touch, usually within 1 to 2 business days.</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => void sendToCaseManager()}
                        disabled={handoff === 'sending'}
                        className="mr-auto flex items-center gap-2 rounded-lg bg-[#0B2341] px-3.5 py-2 text-xs font-medium text-white hover:bg-[#13335c] disabled:opacity-50 transition-colors"
                      >
                        {handoff === 'sending' ? (
                          <><Loader2 size={14} className="animate-spin" /> Sending to {cmFirst}...</>
                        ) : (
                          <><UserRound size={14} /> Send this to {cmFirst}</>
                        )}
                      </button>
                    )
                  )}
                </Fragment>
              ),
            )}
            {loading && (
              <div className="mr-auto flex items-center gap-2 rounded-2xl rounded-tl-sm bg-slate-100 px-3 py-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" />
                Thinking...
              </div>
            )}
            {error && <div className="text-xs text-red-600 px-1">{error}</div>}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={form?.sectionTitle ? 'Ask about this step, formats, or what’s still missing…' : 'Ask a question...'}
                className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-[#1A8DC7] focus:outline-none focus:ring-1 focus:ring-[#1A8DC7] max-h-28"
              />
              <button
                onClick={() => void send()}
                disabled={loading || !input.trim()}
                className="rounded-xl bg-[#1A8DC7] p-2.5 text-white hover:bg-[#157aac] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-tight text-slate-400">
              General information only, not personal migration advice. ADI Immigration,
              Eli Bekker, MARN 1465541.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
